/**
 * ENG-9128 Part A — replace a file so no reader can ever observe it half-written.
 *
 * ## The hazard this removes
 *
 * `deployMcpAssets()` populates `~/.augmented/_mcp` with 14 sequential
 * `copyFileSync(src, dst)` calls. `copyFileSync` **truncates the destination
 * and then writes**, so for each file there is a window in which
 * `~/.augmented/_mcp/slack-channel.js` exists and is empty or partial.
 *
 * That directory is bind-mounted read-only into EVERY agent container on the
 * host (`persistent-session.ts`, `-v ${mcpDir}:${mcpDir}:ro`), so the window is
 * inherited by every agent simultaneously. An agent that spawns inside it loads
 * a corrupt bundle, its MCP servers fail to start, `mcp-presence-reaper`
 * applies its only remedy (restart the session), and three restarts trip the
 * circuit breaker. That is the shape of the 2026-08-19 pauses: `clara` restarted
 * 3× and `razorclaw-ea` 2×, both "to recover MCP server(s): augmented,
 * direct-chat, telegram" — three of the files this loop copies.
 *
 * ## Why this is not already covered by ENG-9125
 *
 * ENG-9125 added `findMissingMcpBundles()`, a pre-spawn gate that defers when a
 * bundle the `.mcp.json` points at is **absent**. A half-written file is *not*
 * absent — `existsSync` returns true — so the gate passes and the agent spawns
 * against it. ENG-9125 closed the empty-mount case; this closes the
 * truncated-file case. They are complementary, and both are needed.
 *
 * ## Why `rename` rather than the ticket's symlink flip
 *
 * ENG-9128 proposes versioned directories published by a symlink swap, and
 * gates that on an untested question: does Docker resolve a bind-mount source
 * symlink at mount time? That question cannot be answered from the agent host
 * (no Docker), and the ticket is explicit that it must not be assumed.
 *
 * `rename(2)` sidesteps it entirely. POSIX guarantees the rename of a file over
 * an existing file within the same filesystem is atomic: a reader sees the old
 * inode or the new one, never a mixture. The directory itself is never
 * replaced, so every existing bind mount is untouched and no migration, GC or
 * symlink is involved.
 *
 * The temp file is created in the SAME DIRECTORY as the destination on purpose.
 * `rename` across filesystems fails with `EXDEV`, and `os.tmpdir()` is very
 * often a different mount — so a "tidier" temp location would turn an atomic
 * replace into a runtime error on exactly the hosts that need it.
 */

import { copyFileSync, renameSync, unlinkSync } from 'node:fs';
import { join, dirname, basename } from 'node:path';

/**
 * Copy `src` over `dst` atomically.
 *
 * Throws on failure, exactly as `copyFileSync` does, so callers keep their
 * existing error handling. On a failed rename the temp file is removed rather
 * than left behind — a stray `.tmp` inside the bundle mount would be a new file
 * for `findMissingMcpBundles` and the reaper to reason about, which is the last
 * thing this directory needs.
 *
 * @param unique - injectable suffix source, so a test can force a collision
 *                 without racing real PIDs.
 */
export function atomicCopyFileSync(
  src: string,
  dst: string,
  unique: () => string = defaultUnique,
): void {
  // Same directory as `dst`, never os.tmpdir(): see the EXDEV note above.
  const tmp = join(dirname(dst), `.${basename(dst)}.tmp-${unique()}`);
  try {
    copyFileSync(src, tmp);
    // The atomic step. Everything before this is invisible to readers of `dst`.
    renameSync(tmp, dst);
  } catch (err) {
    try {
      unlinkSync(tmp);
    } catch {
      // Already gone, or never created. Nothing to clean up, and a cleanup
      // failure must not mask the real error being rethrown below.
    }
    throw err;
  }
}

/**
 * PID + counter + time. Not cryptographic — it only has to avoid a collision
 * between two managers deploying into one shared directory at the same moment,
 * which the PID alone already covers on a single host.
 */
let seq = 0;
function defaultUnique(): string {
  seq += 1;
  return `${process.pid}-${seq}-${Date.now().toString(36)}`;
}
